import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useProfile } from "./get-profile";
import { updateUser } from "./utils";

import type { MutationConfig } from "@/lib/react-query";
import type { User } from "@/types/user";

type UpdateWalkthroughCompletedInput = {
  walkthrough_completed: boolean;
};

type UseUpdateWalkthroughCompletedOptions = {
  mutationConfig?: MutationConfig<(data: UpdateWalkthroughCompletedInput) => Promise<User>>;
};

export const useUpdateWalkthroughCompleted = ({
  mutationConfig,
}: UseUpdateWalkthroughCompletedOptions = {}) => {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const {
    onSuccess,
    onMutate: _onMutate,
    onError: _onError,
    onSettled: _onSettled,
    ...restConfig
  } = mutationConfig || {};

  return useMutation({
    mutationFn: (data: UpdateWalkthroughCompletedInput) =>
      updateUser({
        ...profile,
        additional_info: {
          ...profile?.additional_info,
          walkthrough_completed: data.walkthrough_completed,
        },
      }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["profile"] });
      await queryClient.cancelQueries({ queryKey: ["authenticated-user"] });

      const previousProfile = queryClient.getQueryData<User>(["profile"]);
      const previousAuthedUser = queryClient.getQueryData<User>(["authenticated-user"]);

      if (previousProfile) {
        queryClient.setQueryData<User>(["profile"], {
          ...previousProfile,
          additional_info: {
            ...previousProfile.additional_info,
            walkthrough_completed: variables.walkthrough_completed,
          },
        });
      }

      if (previousAuthedUser) {
        queryClient.setQueryData<User>(["authenticated-user"], {
          ...previousAuthedUser,
          additional_info: {
            ...previousAuthedUser.additional_info,
            walkthrough_completed: variables.walkthrough_completed,
          },
        });
      }

      return { previousProfile, previousAuthedUser };
    },
    onError: (err, variables, context, mutation) => {
      if (context?.previousProfile) queryClient.setQueryData(["profile"], context.previousProfile);
      if (context?.previousAuthedUser)
        queryClient.setQueryData(["authenticated-user"], context.previousAuthedUser);

      _onError?.(err, variables, context, mutation);
    },
    onSuccess: (data, variables, context, mutation) => {
      queryClient.setQueryData(["profile"], data);
      queryClient.setQueryData(["authenticated-user"], data);
      onSuccess?.(data, variables, context, mutation);
    },
    onSettled: (data, error, variables, context, mutation) => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["authenticated-user"] });
      _onSettled?.(data, error, variables, context, mutation);
    },
    ...restConfig,
  });
};
